import { CODES, KEYS } from "../keys";
import {
  isWritableElement,
  getFontString,
  getFontFamilyString,
  isTestEnv,
} from "../utils";
import Scene from "../scene/Scene";
import {
  isArrowElement,
  isBoundToContainer,
  isTextElement,
} from "./typeChecks";
import {CLASSES, isSafari, TEXT_ALIGN} from "../constants";
import {
  ExcalidrawElement,
  ExcalidrawLinearElement,
  ExcalidrawTextElementWithContainer,
  ExcalidrawTextElement,
  ExcalidrawTextContainer,
  RichText,
  EditableDiv,
} from "./types";
import { AppState } from "../types";
import { bumpVersion, mutateElement } from "./mutateElement";
import {
  getBoundTextElementId,
  getContainerElement,
  getTextElementAngle,
  getTextWidth,
  normalizeText,
  redrawTextBoundingBox,
  wrapText,
  getBoundTextMaxHeight,
  getBoundTextMaxWidth,
  computeContainerDimensionForBoundText,
  detectLineHeight,
  computeBoundTextPosition,
} from "./textElement";
import {
  actionDecreaseFontSize,
  actionIncreaseFontSize,
} from "../actions/actionProperties";
import { actionZoomIn, actionZoomOut } from "../actions/actionCanvas";
import App, {resizeHandleType} from "../components/App";
import { LinearElementEditor } from "./linearElementEditor";
import "quill/dist/quill.core.css";
import Quill from "quill";
import { MarkOptional } from "../utility-types";
import {Delta, Op } from "quill/core";
import {postMessage, recvMessage} from "../../../../src/common/event";
import {EVENT_RICH_TEXT_SELECTED} from "../../../../src/Const";
import {ShapeCache} from "../scene/ShapeCache";
import {isDarkMode} from "../../../../src/utils";

const OFFSET_PX = 40;

let quill: Quill | null = null;
let isQuillTextChanged = false;

const getTransform = (
    width: number,
    height: number,
    angle: number,
    appState: AppState,
    maxWidth: number,
    maxHeight: number,
) => {
  const { zoom } = appState;
  const degree = (180 * angle) / Math.PI;
  let translateX = (width * (zoom.value - 1)) / 2;
  let translateY = (height * (zoom.value - 1)) / 2;
  if (width > maxWidth && zoom.value !== 1) {
    translateX = (maxWidth * (zoom.value - 1)) / 2;
  }
  if (height > maxHeight && zoom.value !== 1) {
    translateY = (maxHeight * (zoom.value - 1)) / 2;
  }
  return `translate(${translateX}px, ${translateY}px) scale(${zoom.value}) rotate(${degree}deg)`;
};

const originalContainerCache: {
  [id: ExcalidrawTextContainer["id"]]:
      | {
    height: ExcalidrawTextContainer["height"];
  }
      | undefined;
} = {};

export const updateOriginalContainerCache = (
    id: ExcalidrawTextContainer["id"],
    height: ExcalidrawTextContainer["height"],
) => {
  const data =
      originalContainerCache[id] || (originalContainerCache[id] = { height });
  data.height = height;
  return data;
};

export const resetOriginalContainerCache = (
    id: ExcalidrawTextContainer["id"],
) => {
  if (originalContainerCache[id]) {
    delete originalContainerCache[id];
  }
};

export const getOriginalContainerHeightFromCache = (
    id: ExcalidrawTextContainer["id"],
) => {
  return originalContainerCache[id]?.height ?? null;
};

const getEditableTextValue = (text = "") => {
  return text.slice(0, -1);
};
// function hasNewLineAtEnd(arr: string) {
//   const len = arr.length;
//   return len > 1 && arr[len - 2] === "\n";
// }
export const textWysiwyg = ({
                              id,
                              onChange,
                              onSelect,
                              onSubmit,
                              getViewportCoords,
                              element,
                              canvas,
                              excalidrawContainer,
                              app,
    onWheel,
                            }: {
  id: ExcalidrawElement["id"];
  onSelect?: ({
                range,
              }: {
    range: {
      selectionStart: number;
      selectionEnd: number;
      id: string;
    };
  }) => void;
  onChange?: ({
                text,
                data,
                id,
              }: {
    text: string;
    data: Op[];
    id: string;
  }) => void;
  onSubmit: (data: { text: string; viaKeyboard: boolean; id: string }) => void;
  getViewportCoords: (x: number, y: number) => [number, number];
  element: ExcalidrawTextElement;
  canvas: HTMLCanvasElement;
  excalidrawContainer: HTMLDivElement | null;
  app: App;
  onWheel?:(event:any)=>void;
}) => {
  // 删除绑定元素的文字的逻辑，因为绑定元素的逻辑在textArea文件
  const updateWysiwygStyle = () => {
    const appState = app.state;
    const updatedTextElement =
        Scene.getScene(element)?.getElement<ExcalidrawTextElement>(id);

    if (!updatedTextElement) {
      return;
    }
    const { textAlign, verticalAlign, initialized, richText } = updatedTextElement;

    const hasBreak = richText?.some((item) =>
        ["\n", "\r"].includes(item.value),
    );
    if (updatedTextElement && isTextElement(updatedTextElement)) {
      let coordX = updatedTextElement.x;
      let coordY = updatedTextElement.y;
      let maxWidth = updatedTextElement.width;

      let textElementWidth = updatedTextElement.width;
      // Set to element height by default since that's
      // what is going to be used for unbounded text
      const textElementHeight = updatedTextElement.height;

      const coordXOffset =
          textAlign === "left" ? 0 : textAlign === "center" ? OFFSET_PX / 2 : 0;
      const [viewportX, viewportY] = getViewportCoords(
          coordX - coordXOffset,
          coordY,
      );
      const initialSelectionStart = editable.selectionStart;
      const initialSelectionEnd = editable.selectionEnd;
      const initialLength = editable.value.length;

      // restore cursor position after value updated so it doesn't
      // go to the end of text when container auto expanded
      if (
          initialSelectionStart === initialSelectionEnd &&
          initialSelectionEnd !== initialLength
      ) {
        // get diff between length and selection end and shift
        // the cursor by "diff" times to position correctly
        const diff = initialLength - initialSelectionEnd;
        editable.selectionStart = editable.value.length - diff;
        editable.selectionEnd = editable.value.length - diff;
      }

      maxWidth = (appState.width - 8 - viewportX) / appState.zoom.value;
      textElementWidth = Math.min(textElementWidth, maxWidth);

      let lineHeight = updatedTextElement.lineHeight;

      // In Safari the font size gets rounded off when rendering hence calculating the line height by rounding off font size
      if (isSafari) {
        lineHeight = detectLineHeight({
          ...updatedTextElement,
          fontSize: Math.round(updatedTextElement.fontSize),
        });
      }

      // Make sure text editor height doesn't go beyond viewport
      const editorMaxHeight =
          (appState.height - viewportY) / appState.zoom.value;

      let left = `${viewportX}px`;
      if (initialized || hasBreak) {
        if (textAlign === "left") {
          left = `${viewportX + (1 - appState.zoom.value) * 20}px`;
        } else if (textAlign === "center") {
          left = `${viewportX + 18}px`;
        } else if (textAlign === "right") {
          left =
              appState.zoom.value > 1
                  ? `${viewportX - (appState.zoom.value - 1) * 25.5}px`
                  : `${viewportX - (appState.zoom.value - 1) * 10}px`;
        }
      } else if (textAlign === "right") {
        // console.log(123, viewportX, updatedTextElement.x);
        // // left = `${updatedTextElement.x}px`;
        // const [viewportX1, viewportY] = getViewportCoords(coordX, coordY);
        // console.log(123, viewportX1);
        // left =
        //   appState.zoom.value > 1
        //     ? `${viewportX1 - (appState.zoom.value - 1) * 70}px`
        //     : `${viewportX1 - (appState.zoom.value - 1) * 10}px`;
      }

      const updatedStyle = {
        font: getFontString({ ...updatedTextElement, textareaType: "all" }),
        // must be defined *after* font ¯\_(ツ)_/¯
        lineHeight,
        width: `${
            updatedTextElement.initialized
                ? textElementWidth + 4
                : updatedTextElement.text.split("\n").length > 1
                    ? textElementWidth
                    : textElementWidth + OFFSET_PX
        }px`,
        // height: `${textElementHeight}px`,
        left,
        top: `${viewportY}px`,
        transform: getTransform(
            textElementWidth + OFFSET_PX,
            textElementHeight,
            getTextElementAngle(updatedTextElement),
            appState,
            maxWidth + OFFSET_PX,
            editorMaxHeight,
        ),
        textAlign,
        verticalAlign,
        opacity: updatedTextElement.opacity / 100,
        filter: "var(--theme-filter)",
        maxHeight: `${editorMaxHeight}px`,
      };
      const { width: bodyWidth } = document.body.getBoundingClientRect();
      const { x, width } = editable.getBoundingClientRect();

      const comparedWidth = x + width + OFFSET_PX;
      // textAlign !== "center" ? x + width : x + width + OFFSET_PX / 2;

      if (comparedWidth >= bodyWidth) {
        // @ts-ignore
        updatedStyle.maxWidth = `${bodyWidth - x}px`;

        updatedStyle.left = `${x}px`;
      }
      Object.assign(editable.style, updatedStyle);

      const qlEditorDom = document.getElementsByClassName(
          "ql-editor",
      )[0] as HTMLDivElement;
      if (qlEditorDom) {
        qlEditorDom.style.textAlign = textAlign;
      }

      editable.scrollTop = 0;
      // For some reason updating font attribute doesn't set font family
      // hence updating font family explicitly for test environment
      // if (isTestEnv()) {
        editable.style.fontFamily = getFontFamilyString(updatedTextElement);
      // }
      mutateElement(updatedTextElement, { x: coordX, y: coordY });
    }
  };

  const editable = document.createElement("div") as EditableDiv;

  // editable.onwheel = (event)=>{
  //   if (onWheel){
  //     onWheel(event);
  //   }
  // };

  editable.dir = "auto";
  editable.tabIndex = 0;
  editable.dataset.type = "wysiwyg";
  editable.classList.add("excalidraw-wysiwyg");

  let whiteSpace = "normal";

  //输入框样式
  Object.assign(editable.style, {
    position: "absolute",
    display: "inline-block",
    minHeight: "1em",
    backfaceVisibility: "hidden",
    margin: 0,
    padding: 0,
    border: 0,
    outline: 0,
    resize: "none",
    background: "transparent",
    overflow: "hidden",
    // must be specified because in dark mode canvas creates a stacking context
    zIndex: "var(--zIndex-wysiwyg)",
    // prevent line wrapping (`whitespace: nowrap` doesn't work on FF)
    whiteSpace,
    boxSizing: "content-box",
  });
  // 富文本回显
  editable.value = element.originalText;

  quill = new Quill(editable, {
    theme: "snow",
    modules: {
      toolbar: false,
      keyboard: {
        bindings: {
          "list autofill": {
            key: " ",
            shiftKey: null,
            collapsed: true,
            format: {
              "code-block": false,
              blockquote: false,
              table: false,
            },
            prefix: /^\s*?(\d+\.|-|\*|\[ ?\]|\[x\])$/,
            handler() {
              return true;
            },
          },
          bold: {
            key: [KEYS.B, KEYS.B.toLocaleUpperCase()],
            shortKey: true,
            handler(range: Range, context: any) {
              // @ts-ignore
              this.quill.format(
                  "bold",
                  !context.format.bold,
                  Quill.sources.USER,
              );

              updateRichText({
                textSelectRange: {
                  selectionStart: range.index,
                  selectionEnd: range.index + range.length,
                },
                richText: element?.richText || [],

                actionType: "bold",
                appState: app.state,
              });

              ShapeCache.delete(element);
              // @ts-ignore
              this.quill.setSelection(range.index, range.length);
            },
          },

          italic: {
            key: [KEYS.I, KEYS.I.toLocaleUpperCase()],
            shortKey: true,
            handler(range: Range, context: any) {
              // @ts-ignore
              this.quill.format(
                  "italic",
                  !context.format.italic,
                  Quill.sources.USER,
              );

              updateRichText({
                textSelectRange: {
                  selectionStart: range.index,
                  selectionEnd: range.index + range.length,
                },
                richText: element?.richText || [],

                actionType: "italic",
                appState: app.state,
              });

              ShapeCache.delete(element);
              // @ts-ignore
              this.quill.setSelection(range.index, range.length);
            },
          },

          underline: {
            key: [KEYS.U, KEYS.U.toLocaleUpperCase()],
            shortKey: true,
            handler(range: Range, context: any) {
              // @ts-ignore
              this.quill.format(
                  "underline",
                  !context.format.underline,
                  Quill.sources.USER,
              );

              updateRichText({
                textSelectRange: {
                  selectionStart: range.index,
                  selectionEnd: range.index + range.length,
                },
                richText: element?.richText || [],

                actionType: "underline",
                appState: app.state,
              });

              ShapeCache.delete(element);
              // @ts-ignore
              this.quill.setSelection(range.index, range.length);
            },
          },
          textAlignCenter: {
            key: [KEYS.C, KEYS.C.toLocaleUpperCase()],
            shortKey: true,
            shiftKey: true,
            handler() {
              const updatedTextElement =
                  Scene.getScene(element)?.getElement<ExcalidrawTextElement>(id)!;
              mutateElement(updatedTextElement, {
                textAlign: TEXT_ALIGN.CENTER,
              });
              ShapeCache.delete(updatedTextElement);
            },
          },
          textAlignRight: {
            key: [KEYS.R, KEYS.R.toLocaleUpperCase()],
            shortKey: true,
            shiftKey: true,
            handler() {
              const updatedTextElement =
                  Scene.getScene(element)?.getElement<ExcalidrawTextElement>(id)!;
              mutateElement(updatedTextElement, {
                textAlign: TEXT_ALIGN.RIGHT,
              });
              ShapeCache.delete(updatedTextElement);
            },
          },
        },
      },
    },
  });
// 处理富文本文字和text文字保持一致，富文本回显
  const a = normalizeRichText(element);
  element.richText = a;
  const richText =
      element.richText?.map((text) => {
        const reslut = {
          insert: text.value,
          attributes: {
            bold: !!text.bold,
            color: text.color,
            italic: !!text.italic,
            underline: !!text.underline,
            strike: !!text.strikeout,
          },
        };
        return reslut;
      }) || [];
  quill.setContents(richText);

  const qlEditor = editable.children[0] as HTMLDivElement;
  qlEditor.style.lineHeight = String(element.lineHeight);
  qlEditor.style.color = isDarkMode()?'#ffffff':'#000000';

  updateWysiwygStyle();

  if (onChange) {

    qlEditor.oninput = (event) => {
      if (isQuillTextChanged) {
        isQuillTextChanged = false;
        return;
      }
      onChange({
        // @ts-ignore
        text: normalizeText(event.target.innerText),
        // @ts-ignore
        data: event.data.split(""),
        id: element.id,
      });
    };

    quill?.on("text-change", (delta, oldDelta, source) => {
      if (source === "api") {
        return;
      }
      isQuillTextChanged = true;

      const text = getEditableTextValue(quill!.getText());
      // const insertString = delta.ops.find((item) => item.insert)
      //   ?.insert as string;

      editable.value = text;

      const textArr = text.split("");

      if (textArr[textArr.length - 2] && textArr[textArr.length - 2] === "\n") {
        if (delta.ops[0].retain) {
          const retain = Number(delta.ops[0].retain);

          const text = element.richText![retain - 2]!;

          quill!.formatText(retain, retain + 1, {
            bold: !!text.bold,
            color: text.color,
            italic: !!text.italic,
            underline: !!text.underline,
            strike: !!text.strikeout,
          });
        }
      }


      onChange({
        text: normalizeText(editable.value),
        data: delta.ops,
        id: element.id,
      });
    });

    // quill?.root.addEventListener('paste',(e)=>{
    //   e.preventDefault();
    //   e.stopImmediatePropagation();
    //   // 2. 获取剪贴板数据
    //   const text = e.clipboardData.getData('text/plain');
    //   const html = e.clipboardData.getData('text/html');
    //
    //   // 4. 插入修改后的内容
    //   // 方法A: 使用Quill API插入内容
    //   const range = quill?.getSelection();
    //   quill?.insertText(range.index, text);
    // })

    quill.clipboard.addMatcher('*',(node,delta)=>{
      console.log(delta);
      const fullContent = delta.ops.map(op => op.insert).join('');
      const processed = (fullContent);
      return new Delta().insert(processed);
    })

    quill?.on("selection-change", (range) => {
      if (!range || !quill?.hasFocus()) {
        return;
      }

      // console.log(range,'range');
      editable.selectionStart = range.index;
      editable.selectionEnd = range.index + range.length;
      postMessage(EVENT_RICH_TEXT_SELECTED, {hasSelection:(editable.selectionEnd > editable.selectionStart)});

      onSelect?.({
        range: {
          selectionStart: editable.selectionStart,
          selectionEnd: editable.selectionEnd,
          id,
        },
      });
    });
  }

  qlEditor.onkeydown = (event) => {
    if (!event.shiftKey && actionZoomIn.keyTest(event)) {
      event.preventDefault();
      app.actionManager.executeAction(actionZoomIn);
      updateWysiwygStyle();
    } else if (!event.shiftKey && actionZoomOut.keyTest(event)) {
      event.preventDefault();
      app.actionManager.executeAction(actionZoomOut);
      updateWysiwygStyle();
    } else if (actionDecreaseFontSize.keyTest(event)) {
      app.actionManager.executeAction(actionDecreaseFontSize);
    } else if (actionIncreaseFontSize.keyTest(event)) {
      app.actionManager.executeAction(actionIncreaseFontSize);
    } else if (event.key === KEYS.ESCAPE) {
      event.preventDefault();
      submittedViaKeyboard = true;
      handleSubmit();
    } else if (event.key === KEYS.ENTER && event[KEYS.CTRL_OR_CMD]) {
      event.preventDefault();
      if (event.isComposing || event.keyCode === 229) {
        return;
      }
      submittedViaKeyboard = true;
      handleSubmit();
    } else if (
        event.key === KEYS.TAB ||
        (event[KEYS.CTRL_OR_CMD] &&
            (event.code === CODES.BRACKET_LEFT ||
                event.code === CODES.BRACKET_RIGHT))
    ) {
      event.preventDefault();
      if (event.isComposing) {
        return;
      } else if (event.shiftKey || event.code === CODES.BRACKET_LEFT) {
        outdent();
      } else {
        indent();
      }
      // We must send an input event to resize the element
      editable.dispatchEvent(new Event("input"));
    }
  };

  const TAB_SIZE = 4;
  const TAB = " ".repeat(TAB_SIZE);
  const RE_LEADING_TAB = new RegExp(`^ {1,${TAB_SIZE}}`);
  const indent = () => {
    const { selectionStart, selectionEnd } = editable;
    const linesStartIndices = getSelectedLinesStartIndices();

    let value = editable.value;
    linesStartIndices.forEach((startIndex: number) => {
      const startValue = value.slice(0, startIndex);
      const endValue = value.slice(startIndex);

      value = `${startValue}${TAB}${endValue}`;
    });

    // 富文本回显
    editable.value = value;

    editable.selectionStart = selectionStart + TAB_SIZE;
    editable.selectionEnd = selectionEnd + TAB_SIZE * linesStartIndices.length;
  };

  const outdent = () => {
    const { selectionStart, selectionEnd } = editable;
    const linesStartIndices = getSelectedLinesStartIndices();
    const removedTabs: number[] = [];

    let value = editable.value;
    linesStartIndices.forEach((startIndex) => {
      const tabMatch = value
          .slice(startIndex, startIndex + TAB_SIZE)
          .match(RE_LEADING_TAB);

      if (tabMatch) {
        const startValue = value.slice(0, startIndex);
        const endValue = value.slice(startIndex + tabMatch[0].length);

        // Delete a tab from the line
        value = `${startValue}${endValue}`;
        removedTabs.push(startIndex);
      }
    });
    // 富文本回显
    editable.value = value;

    if (removedTabs.length) {
      if (selectionStart > removedTabs[removedTabs.length - 1]) {
        editable.selectionStart = Math.max(
            selectionStart - TAB_SIZE,
            removedTabs[removedTabs.length - 1],
        );
      } else {
        // If the cursor is before the first tab removed, ex:
        // Line| #1
        //     Line #2
        // Lin|e #3
        // we should reset the selectionStart to his initial value.
        editable.selectionStart = selectionStart;
      }
      editable.selectionEnd = Math.max(
          editable.selectionStart,
          selectionEnd - TAB_SIZE * removedTabs.length,
      );
    }
  };

  /**
   * @returns indices of start positions of selected lines, in reverse order
   */
  const getSelectedLinesStartIndices = () => {
    let { selectionStart, selectionEnd, value } = editable;

    // chars before selectionStart on the same line
    const startOffset = value.slice(0, selectionStart).match(/[^\n]*$/)![0]
        .length;
    // put caret at the start of the line
    selectionStart = selectionStart - startOffset;

    const selected = value.slice(selectionStart, selectionEnd);

    return selected
        .split("\n")
        .reduce(
            (startIndices, line, idx, lines) =>
                startIndices.concat(
                    idx
                        ? // curr line index is prev line's start + prev line's length + \n
                        startIndices[idx - 1] + lines[idx - 1].length + 1
                        : // first selected line
                        selectionStart,
                ),
            [] as number[],
        )
        .reverse();
  };

  const stopEvent = (event: Event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  // using a state variable instead of passing it to the handleSubmit callback
  // so that we don't need to create separate a callback for event handlers
  let submittedViaKeyboard = false;
  const handleSubmit = () => {
    // cleanup must be run before onSubmit otherwise when app blurs the wysiwyg
    // it'd get stuck in an infinite loop of blur→onSubmit after we re-focus the
    // wysiwyg on update
    cleanup();
    const updateElement = Scene.getScene(element)?.getElement(
        element.id,
    ) as ExcalidrawTextElement;
    if (!updateElement) {
      return;
    }
    const text = editable.value;
    onSubmit({
      text,
      viaKeyboard: submittedViaKeyboard,
      id: element.id,
    });
    quill = null;
  };

  const cleanup = () => {
    if (isDestroyed) {
      return;
    }
    isDestroyed = true;
    // remove events to ensure they don't late-fire
    qlEditor.onblur = null;
    qlEditor.onkeydown = null;

    if (observer) {
      observer.disconnect();
    }

    window.removeEventListener("resize", updateWysiwygStyle);
    window.removeEventListener("wheel", stopEvent, true);
    window.removeEventListener("pointerdown", onPointerDown);
    window.removeEventListener("pointerup", bindBlurEvent);
    window.removeEventListener("blur", handleSubmit);
    window.removeEventListener("beforeunload", handleSubmit);
    unbindUpdate();

    editable.remove();
  };

  const bindBlurEvent = (event?: MouseEvent) => {
    window.removeEventListener("pointerup", bindBlurEvent);
    // Deferred so that the pointerdown that initiates the wysiwyg doesn't
    // trigger the blur on ensuing pointerup.
    // Also to handle cases such as picking a color which would trigger a blur
    // in that same tick.

    const target = event?.target;
    const isTargetPickerTrigger =
        target instanceof HTMLElement &&
        (target.classList.contains("active-color") ||
            target.classList.contains("ant-select-selection-item"));
    // const isTargetPickerTrigger = event?.isTargetPickerTigger;

    setTimeout(() => {
      qlEditor.onblur = () => {
        setTimeout(() => {
          const hasFocus = quill?.hasFocus();
          if (!hasFocus) {
            handleSubmit();
          }
        }, 100);
      };

      if (isTargetPickerTrigger) {
        const callback = (
            mutationList: MutationRecord[],
            observer: MutationObserver,
        ) => {
          const radixIsRemoved = mutationList.find((mutation) => {
            if (mutation.removedNodes.length === 0) {
              return false;
            }

            return (
                (mutation.removedNodes[0] as HTMLElement).dataset
                    ?.radixPopperContentWrapper !== undefined ||
                (mutation.removedNodes[0] as HTMLElement).id === "shadow_dom"
            );
          });

          if (radixIsRemoved) {
            // should work without this in theory
            // and i think it does actually but radix probably somewhere,
            // somehow sets the focus elsewhere
            setTimeout(() => {
              quill?.setSelection(
                  editable.selectionStart,
                  editable.selectionEnd - editable.selectionStart,
              );
            });

            observer.disconnect();
          }
        };

        const observer = new MutationObserver(callback);

        observer.observe(document.querySelector(".excalidraw-container")!, {
          childList: true,
        });
      }

      // case: clicking on the same property → no change → no update → no focus
      if (!isTargetPickerTrigger) {
        if (quill?.getSelection()?.length > 0){
          quill?.focus();
        }else{
          const length = quill?.getLength() || 100;
          quill?.setSelection(0, length);
        }
      }
    });
  };

  window.onBindBlurEvent = bindBlurEvent;

  // prevent blur when changing properties from the menu
  const onPointerDown = (event: MouseEvent) => {
    const isTargetPickerTrigger =
        event.target instanceof HTMLElement &&
        event.target.classList.contains("active-color");

    if (
        ((event.target instanceof HTMLElement ||
                event.target instanceof SVGElement) &&
            event.target.closest(`.${CLASSES.SHAPE_ACTIONS_MENU}`) &&
            !isWritableElement(event.target)) ||
        isTargetPickerTrigger
    ) {
      qlEditor.onblur = null;
      window.addEventListener("pointerup", bindBlurEvent);
      // handle edge-case where pointerup doesn't fire e.g. due to user
      // alt-tabbing away
      window.addEventListener("blur", handleSubmit);
    }
  };
  window.onPointerDownEvent = onPointerDown;

  // handle updates of textElement properties of editing element
  const unbindUpdate = Scene.getScene(element)!.addCallback(() => {
    updateWysiwygStyle();
    // const isColorPickerActive = !!document.activeElement?.closest(
    //   ".color-picker-content",
    // );

    // if (!isColorPickerActive) {
    // quill?.focus();
    // }
  });

  // ---------------------------------------------------------------------------

  let isDestroyed = false;

  // select on init (focusing is done separately inside the bindBlurEvent()
  // because we need it to happen *after* the blur event from `pointerdown`)
  // quill?.focus();
  bindBlurEvent();
  setTimeout(() => {
    const length = quill?.getLength() || 100;
    quill?.setSelection(0, length);
  }, 0);

  // reposition wysiwyg in case of canvas is resized. Using ResizeObserver
  // is preferred so we catch changes from host, where window may not resize.
  let observer: ResizeObserver | null = null;
  if (canvas && "ResizeObserver" in window) {
    observer = new window.ResizeObserver(() => {
      updateWysiwygStyle();
    });
    observer.observe(canvas);
  } else {
    window.addEventListener("resize", updateWysiwygStyle);
  }

  window.addEventListener("pointerdown", onPointerDown);
  window.addEventListener("wheel", stopEvent, {
    passive: false,
    capture: true,
  });
  window.addEventListener("beforeunload", handleSubmit);
  excalidrawContainer
      ?.querySelector(".excalidraw-textEditorContainer")!
      .appendChild(editable);
};

export const updateRichText = ({
                                 textSelectRange,
                                 richText,
                                 actionType,
                                 appState,
                               }: {
  textSelectRange: {
    selectionStart: number;
    selectionEnd: number;
  };
  richText: RichText[];
  actionType: "color" | "bold" | "italic" | "underline" | "strikeout";
  appState: MarkOptional<
      AppState,
      "width" | "height" | "offsetTop" | "offsetLeft"
  >;
}) => {
  const { selectionStart, selectionEnd } = textSelectRange;
  const selectedText = richText.slice(selectionStart, selectionEnd);

  // 动态生成检查逻辑
  const areAllSameActionType = selectedText.every((item) =>
      Object.prototype.hasOwnProperty.call(item, actionType)
          ? item[actionType] === selectedText[0][actionType]
          : true,
  );

  // 更新richText中的样式
  for (let i = selectionStart; i < selectionEnd; i++) {
    const curText = richText[i];
    if (actionType === "color") {
      // curText.color = appState?.currentItemStrokeColor;
      curText.color = appState?.currentItemFontColor;
    } else if (
        ["bold", "italic", "underline", "strikeout"].includes(actionType)
    ) {
      // 如果选中的文本样式不一致，则直接设置为true，否则取反
      curText[actionType] = !areAllSameActionType || !curText[actionType];
    }
  }

  // 更新quill的内容
  const newRichText = richText.map((text) => {
    return {
      insert: text.value,
      attributes: {
        bold: !!text.bold,
        color: text.color,
        italic: !!text.italic,
        underline: !!text.underline,
        strike: !!text.strikeout,
      },
    };
  });

  quill?.setContents(newRichText);
  //选中
  quill?.setSelection(textSelectRange);
  quill?.focus();
};

export function normalizeRichText(element: ExcalidrawTextElement) {
  // 如果处理右侧宽度，增加文字的换行符
  if (resizeHandleType === "e") {
    const text = wrapText(
        element.originalText,
        getFontString(element),
        element.width,
        element,
    );

    // const { width, height } = getAdjustedDimensions(element, text);

    mutateElement(element, {
      text,
      // width,
      // height,
    });
  }

  const normalizedText =
      element.richText?.filter(
          (item) => item.value !== "\n" && item.value !== " ",
          // item.value !== "-" &&
          // item.value !== "—",
      ) || [];

  element.text?.split("").forEach((item, index) => {
    const currentRichText = normalizedText[index];

    if (
        item === currentRichText?.value ||
        (item === "\n" && currentRichText?.value === "\r")
    ) {
      return;
    }
    // if (
    //   item === "-" ||
    //   currentRichText.value === "—" ||
    //   item === "—" ||
    //   currentRichText.value === "-"
    // ) {
    //   return;
    // }

    normalizedText.splice(index, 0, {
      ...currentRichText,
      value: item,
    });
  });
  // 处理富文本的将富文本的内容和text保持一致
  return normalizedText;
}

function mergeEmojiItems(items: any[]) {
  const mergedItems: any[] = [];
  let emojiBuffer: any[] = [];

  items.forEach((item: any) => {
    // 检查当前项是否为emoji的一部分
    if (!item.emoji || item.emoji.length === 1) {
      mergedItems.push(item);
      emojiBuffer = [];
      return;
    }

    // 将当前项添加到emoji缓冲区
    emojiBuffer.push(item);
    // 如果这是emoji的最后一个部分，则合并并添加到结果数组
    if (item.emoji.indice === item.emoji.length - 1) {
      mergedItems.push({
        value: emojiBuffer.map((part) => part.value).join(""),
        fontFamily: item.fontFamily,
        fontSize: item.fontSize,
        color: item.color,
        bold: item.bold,
        italic: item.italic,
        underline: item.underline,
        strikeout: item.strikeout,
      });
      // 清空emoji缓冲区
      emojiBuffer = [];
    }
  });

  return mergedItems;
}

export function normalizeRichTextWithEmoji(element: ExcalidrawTextElement) {
  const normalizedText = normalizeRichText(element);
  return mergeEmojiItems(normalizedText);
}
